import React, {
  MouseEventHandler,
  ReactNode,
  RefObject,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";
import AssetCover, { CoverStyle, ImageSource } from "./AssetCover.tsx";
import BaseLinkOrButton from "../../core-ui/BaseLinkOrButton.tsx";
import { clsx } from "clsx";
import styles from "./EmptyAssetCard.module.scss";

interface EmptyAssetCardProps {
  type: "auto" | "vertical" | "horizontal";
  lazyLoad?: boolean;
  primaryAction?: {
    href?: string;
    onClick?: MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>;
    to?: string;
  };
  vbid: string;
  imageSource?: ImageSource;
  imageChildren?: ReactNode;
  imageGarnish?: ReactNode;
  children?: ReactNode;
  contentAreaClassName?: string;
  tagName: "li" | "div";
  horizontalCardImageWidth?: string;
  desiredImageAspectRatio?: number;
  className?: string;
  imageAddendum?: ReactNode;
  coverStyle?: CoverStyle;
}

const EmptyAssetCard = (
  {
    type,
    lazyLoad,
    primaryAction,
    vbid,
    imageChildren,
    imageGarnish,
    children,
    contentAreaClassName,
    tagName: TagName,
    horizontalCardImageWidth,
    desiredImageAspectRatio,
    className,
    imageAddendum,
    coverStyle,
    imageSource,
  }: EmptyAssetCardProps,
  ref: React.ForwardedRef<HTMLElement>
) => {
  const imageRef = useRef<HTMLImageElement>(null);
  const [isImageLoaded, setIsImageLoaded] = useState(false);

  const onImageLoaded = useCallback(() => {
    setIsImageLoaded(true);
  }, []);

  const loadedImageAspectRatio = useMemo(() => {
    if (imageRef.current && isImageLoaded) {
      return imageRef.current.naturalWidth / imageRef.current.naturalHeight;
    }
  }, [isImageLoaded]);

  const cssConfig: Record<string, string | number> = {};
  if (desiredImageAspectRatio) {
    cssConfig["--vst-book-card-image-aspect-ratio"] = desiredImageAspectRatio;
  }
  if (horizontalCardImageWidth) {
    cssConfig["--vst-book-card-horizontal-image-width"] =
      horizontalCardImageWidth;
  }

  return (
    <TagName
      // I don't think this is ideal, but ref with dynamic tag is being tricky
      ref={ref as RefObject<HTMLLIElement> & RefObject<HTMLDivElement>}
      className={clsx(
        type === "vertical"
          ? "vst-book-card-vertical"
          : type === "horizontal"
          ? "vst-book-card-horizontal"
          : "vst-book-card-auto",
        {
          "vst-book-card-cover-fit-mode-contain":
            loadedImageAspectRatio && loadedImageAspectRatio >= 1,
        },
        className
      )}
      style={cssConfig}
    >
      <div className="vst-book-card-region-image-outer">
        <div className="vst-book-card-region-image-holder-container">
          {imageGarnish && (
            <div className="vst-book-card-region-image-garnish">
              {imageGarnish}
            </div>
          )}
          <div className="vst-book-card-region-image-holder">
            <AssetCover
              width={240}
              width2x={480}
              vbid={vbid}
              alt={""}
              loading={lazyLoad ? "lazy" : undefined}
              onLoad={onImageLoaded}
              ref={imageRef}
              className="vst-book-card-component-image"
              imageStyle={coverStyle}
              source={imageSource}
            />
            {imageChildren}
          </div>
          {/* The current expectation is that the primary action is a mouse user convenience only, that
            this action is also present elsewhere on the card */}
          {primaryAction && (
            <BaseLinkOrButton
              onClick={primaryAction.onClick}
              href={primaryAction.href}
              to={primaryAction.to}
              className="vst-book-card-component-image-button"
              aria-hidden={true}
              tabIndex={-1}
            ></BaseLinkOrButton>
          )}
        </div>
        {imageAddendum && (
          <div className={"vst-book-card-region-image-addendum"}>
            {imageAddendum}
          </div>
        )}
      </div>
      <div
        className={clsx(
          "vst-book-card-region-bookinfo",
          styles.bookInfoArea,
          contentAreaClassName
        )}
      >
        {children}
      </div>
    </TagName>
  );
};

const ForwardedBaseAssetCard = React.forwardRef<
  HTMLElement,
  EmptyAssetCardProps
>(EmptyAssetCard);
export default ForwardedBaseAssetCard;
